import { useCallback } from 'react';
import { useImpersonationContext } from '@/contexts/ImpersonationContext';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook to access impersonation functionality
 * Provides helper functions to get effective user ID and check impersonation status
 */
export function useImpersonation() {
  const { user } = useAuth();
  const {
    impersonatedUserId,
    impersonatedUserEmail,
    isImpersonating,
    startedAt,
    reason,
    remainingTimeMs,
    startImpersonation,
    stopImpersonation,
  } = useImpersonationContext();

  /**
   * Get the effective user ID to use for data queries
   * Returns impersonated user ID if impersonating, otherwise current user ID
   */
  const getEffectiveUserId = useCallback((): string | null => {
    if (isImpersonating && impersonatedUserId) {
      return impersonatedUserId;
    }
    return user?.id || null;
  }, [isImpersonating, impersonatedUserId, user?.id]);

  /**
   * Get the effective user email for display purposes
   */
  const getEffectiveUserEmail = useCallback((): string | null => {
    if (isImpersonating && impersonatedUserEmail) {
      return impersonatedUserEmail;
    }
    return user?.email || null;
  }, [isImpersonating, impersonatedUserEmail, user?.email]);

  /**
   * Get formatted remaining time (e.g., "25:30")
   */
  const getFormattedRemainingTime = useCallback((): string => {
    const minutes = Math.floor(remainingTimeMs / 60000);
    const seconds = Math.floor((remainingTimeMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [remainingTimeMs]);

  return {
    impersonatedUserId,
    impersonatedUserEmail,
    isImpersonating,
    startedAt,
    reason,
    remainingTimeMs,
    getEffectiveUserId,
    getEffectiveUserEmail,
    getFormattedRemainingTime,
    startImpersonation,
    stopImpersonation,
  };
}
